import { t } from "@lingui/macro"
import { useMemo, useCallback } from "react"

import { GridRenderCellParams } from "@mui/x-data-grid-pro"

import { IContractTableItem } from "src/views/contracts/pages/list/types/contract-list"
import { IColumn } from "src/types/data-grid-pro"
import { RowCellContainer } from "src/components/RowCellContainer"
import { formatDate } from "src/lib/date"
import { StatusChip } from "src/components/StatusChip"
import { useGetContractStatus } from "src/views/contracts/hooks/useGetContractStatus"
import { Time } from "src/components/Time"

export const useContractColumns = (): IColumn<IContractTableItem>[] => {
    const { getStatusState } = useGetContractStatus()

    const renderTextCell = useCallback(
        (params: GridRenderCellParams<IContractTableItem>, fallback = "-") => (
            <RowCellContainer>{params.value ?? fallback}</RowCellContainer>
        ),
        [],
    )

    const renderDateCell = useCallback(
        (params: GridRenderCellParams<IContractTableItem>) =>
            Boolean(params.value) ? <Time date={params.value} /> : "-",
        [],
    )

    const renderFormattedDateCell = useCallback(
        (params: GridRenderCellParams<IContractTableItem>) => (
            <RowCellContainer>
                {Boolean(params.value) ? formatDate(params.value) : "-"}
            </RowCellContainer>
        ),
        [],
    )

    const renderStatusCell = useCallback(
        (params: GridRenderCellParams<IContractTableItem>) => {
            if (!Boolean(params.value)) {
                return <RowCellContainer>-</RowCellContainer>
            }

            return (
                <RowCellContainer>
                    <StatusChip
                        state={getStatusState(params.value)}
                        label={params.value}
                    />
                </RowCellContainer>
            )
        },
        [getStatusState],
    )

    return useMemo(() => {
        const columns: IColumn<IContractTableItem>[] = [
            {
                field: "id",
                headerName: t`contracts-list-view.columns.id`,
                renderCell: (params) => renderTextCell(params),
                type: "number",
                minWidth: 150,
                flex: 0.5,
            },
            {
                field: "agreement_no",
                headerName: t`contracts-list-view.columns.agreement-no`,
                renderCell: (params) => renderTextCell(params),
                type: "string",
                minWidth: 200,
                flex: 1,
            },
            {
                field: "property_owner_name",
                headerName: t`contracts-list-view.columns.property-owner`,
                renderCell: (params) => renderTextCell(params),
                type: "string",
                minWidth: 180,
                flex: 1,
            },
            {
                field: "classification",
                headerName: t`contract-details.object-type`,
                renderCell: (params) => renderTextCell(params),
                type: "string",
                minWidth: 150,
                flex: 1,
            },
            {
                field: "registration_date",
                headerName: t`contracts-list-view.columns.registration-date`,
                renderCell: renderDateCell,
                type: "date",
                minWidth: 150,
                flex: 1,
            },
            {
                field: "lease_from",
                headerName: t`contracts-list-view.columns.lease-from`,
                renderCell: renderDateCell,
                type: "date",
                minWidth: 150,
                flex: 1,
            },
            {
                field: "lease_to",
                headerName: t`contracts-list-view.columns.lease-to`,
                renderCell: renderFormattedDateCell,
                type: "date",
                minWidth: 150,
                flex: 1,
            },
            {
                field: "status",
                headerName: t`contracts-list-view.columns.status`,
                renderCell: renderStatusCell,
                type: "string",
                minWidth: 130,
                flex: 1,
            },
            {
                field: "tenants",
                headerName: t`contracts-list-view.columns.tenants`,
                renderCell: (params) => renderTextCell(params),
                type: "string",
                minWidth: 180,
                flex: 1,
            },
            {
                field: "legal_entity_name",
                headerName: t`contracts-list-view.columns.legal-entity`,
                renderCell: (params) => renderTextCell(params),
                type: "string",
                minWidth: 180,
                flex: 1,
            },
            {
                field: "property_object_id",
                headerName: t`contracts-list-view.columns.property-id`,
                renderCell: (params) => renderTextCell(params),
                type: "string",
                minWidth: 130,
                flex: 0.8,
            },
        ]

        return columns
    }, [
        renderTextCell,
        renderDateCell,
        renderFormattedDateCell,
        renderStatusCell,
    ])
}
